import { useCoachRoster } from '../../api/useCoachRoster'
import { AdvancedCareTeamMember, AdvancedTrackDefinition } from '../../api/types'
import { roleLabelFor } from '../careTeam/roleCategories'

export type LeadOption = {
  providerUid: string
  label: string
  joinAs: string | null
  group: 'Care team' | 'Add to care team'
}

export type LeadDirectory = {
  nameFor: (providerUid: string) => string
  // "Jane Doe · Nutritionist", or a note when that provider has since left the care team.
  leadLabel: (providerUid: string | null) => string | null
  optionsFor: (track: AdvancedTrackDefinition | undefined) => LeadOption[]
}

export const useLeadDirectory = (careTeam: AdvancedCareTeamMember[]): LeadDirectory => {
  const roster = useCoachRoster()

  const nameFor = (providerUid: string) => roster.find((coach) => coach.id === providerUid)?.name ?? providerUid
  const rolesOf = (providerUid: string) =>
    careTeam.filter((member) => member.providerUid === providerUid).map((member) => member.role)

  const leadLabel = (providerUid: string | null) => {
    if (!providerUid) {
      return null
    }
    const roles = rolesOf(providerUid)

    return roles.length > 0
      ? `${nameFor(providerUid)} · ${roles.map(roleLabelFor).join(', ')}`
      : `${nameFor(providerUid)} (no longer on the care team)`
  }

  // Owner-role members first; the roster is offered only when joining has a role to join as.
  const optionsFor = (track: AdvancedTrackDefinition | undefined): LeadOption[] => {
    const ownerRole = track?.ownerRole ?? null
    const owns = (providerUid: string) => ownerRole !== null && rolesOf(providerUid).includes(ownerRole)
    const members = [...new Set(careTeam.map((member) => member.providerUid))]
      .sort((a, b) => Number(owns(b)) - Number(owns(a)) || nameFor(a).localeCompare(nameFor(b)))
      .map((providerUid) => ({
        providerUid,
        label: leadLabel(providerUid) ?? providerUid,
        joinAs: null,
        group: 'Care team' as const,
      }))
    const joinable = ownerRole
      ? roster
          .filter((coach) => !careTeam.some((member) => member.providerUid === coach.id))
          .map((coach) => ({
            providerUid: coach.id,
            label: `${coach.name} — adds them as ${roleLabelFor(ownerRole)}`,
            joinAs: ownerRole,
            group: 'Add to care team' as const,
          }))
      : []

    return [...members, ...joinable]
  }

  return { nameFor, leadLabel, optionsFor }
}
